/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  axios,
  AxiosResponsePromise,
  AxiosRequestExtraConfig,
  AxiosRequestConfig,
} from '@seiue/axios'
import {
  createConfigBuilder,
  useQueryApi,
  useMutationApi,
  UseQueryApiReturns,
} from '../utils'
import { MutationOptions, QueryOptions, Expand } from '../types'
import { Entrydevice } from './entrydevice'
import { Entryrecord } from './entryrecord'
import { CountReflection } from './count-reflection'
import { UpdatePassReq } from './update-pass-req'
import { Reflection } from './reflection'
import { ReflectionAdd } from './reflection-add'
import { BatchUpdateWithIdReq } from './batch-update-with-id-req'
import { BatchUpdateWithIdStatusReq } from './batch-update-with-id-status-req'
import { UploadFile } from './upload-file'
import { BatchResult } from './batch-result'
import { ReflectionPhone } from './reflection-phone'
import { ReflectionPhoneRes } from './reflection-phone-res'
import { ReflectionReq } from './reflection-req'
import { QueryMeEntryrecordsQuery } from './query-me-entryrecords-query'
import { CountReflectionsQuery } from './count-reflections-query'
import { QueryStudentsQuery } from './query-students-query'
import { BatchDisableStudentsQuery } from './batch-disable-students-query'
import { QueryImportedStudentsQuery } from './query-imported-students-query'
import { ImportedStudentsQuery } from './imported-students-query'
import { QueryStudentsListQuery } from './query-students-list-query'
import { LoadStudentQuery } from './load-student-query'
import { QueryTeachersQuery } from './query-teachers-query'
import { BatchDisableTeachersQuery } from './batch-disable-teachers-query'
import { ImportedTeachersQuery } from './imported-teachers-query'
import { QueryImportedTeachersQuery } from './query-imported-teachers-query'
import { QueryTeachersListQuery } from './query-teachers-list-query'
import { LoadTeacherQuery } from './load-teacher-query'

const buildConfig = createConfigBuilder('growp')

export const reflectionApi = {
  queryMeEntrydevicesConfig(
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/reflection/entrydevices/me', {})
  },

  /**
   * @summary 正式用户-个人登录设备查询
   *  - 所有记录
   *  - 不含分页
   * @param [options]
   * @return AxiosResponsePromise<Entrydevice[]>
   */
  queryMeEntrydevices(
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Entrydevice[]> {
    return axios(reflectionApi.queryMeEntrydevicesConfig(options)).then(
      ({ data: _data, ...rest }) => {
        const data = _data ? _data.map((d: any) => new Entrydevice(d)) : _data
        return { data, ...rest }
      }
    ) as any
  },

  useQueryMeEntrydevices(
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Entrydevice[]> {
    return useQueryApi<Entrydevice[]>(
      reflectionApi.queryMeEntrydevices,
      'Reflection.queryMeEntrydevices',
      queryOptions
    )
  },

  queryMeEntryrecordsConfig(
    queryMeEntryrecordsQuery?: QueryMeEntryrecordsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/entryrecords/me',
      {},
      queryMeEntryrecordsQuery
    )
  },

  /**
   * @summary 正式用户-个人登录记录查询
   *  - 近 6 个月
   *  - 含分页
   * @param [queryMeEntryrecordsQuery]
   * @param [queryMeEntryrecordsQuery.operatedAtEgt] 登录时间
   * @param [queryMeEntryrecordsQuery.operatedAtElt]
   * @param [queryMeEntryrecordsQuery.paginated] 是否分页，默认 1
   * @param [queryMeEntryrecordsQuery.page] 显示的数据页，默认 1
   * @param [queryMeEntryrecordsQuery.perPage] 每页显示数量，默认 20
   * @param [queryMeEntryrecordsQuery.sort]
   * @param [options]
   * @return AxiosResponsePromise<Entryrecord[]>
   */
  queryMeEntryrecords(
    queryMeEntryrecordsQuery?: QueryMeEntryrecordsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Entryrecord[]> {
    return axios(
      reflectionApi.queryMeEntryrecordsConfig(queryMeEntryrecordsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Entryrecord(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryMeEntryrecords(
    query?: QueryMeEntryrecordsQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Entryrecord[]> {
    return useQueryApi<Entryrecord[]>(
      reflectionApi.queryMeEntryrecords,
      'Reflection.queryMeEntryrecords',
      queryOptions,
      query
    )
  },

  countReflectionsConfig(
    countReflectionsQuery?: CountReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/reflections/count',
      {},
      countReflectionsQuery
    )
  },

  /**
   * @summary 查询正式用户数据统计信息
   * @param [countReflectionsQuery]
   * @param [countReflectionsQuery.status] 状态查询
   * @param [countReflectionsQuery.archivedAtNotEmpty] 传 true 查离校用户
   * @param [countReflectionsQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<CountReflection>
   */
  countReflections(
    countReflectionsQuery?: CountReflectionsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<CountReflection> {
    return axios(
      reflectionApi.countReflectionsConfig(countReflectionsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new CountReflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCountReflections(
    query?: CountReflectionsQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<CountReflection> {
    return useQueryApi<CountReflection>(
      reflectionApi.countReflections,
      'Reflection.countReflections',
      queryOptions,
      query
    )
  },

  updateReflectionPassConfig(
    role: string,
    id: number,
    updatePassReq: UpdatePassReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/reflection/reflections/{id}/password/{role}',
      {
        role,
        id,
      },
      null,
      updatePassReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          schoolId: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 管理员修改正式用户密码
   * @param role
   * @param id
   * @param updatePassReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  updateReflectionPass(
    role: string,
    id: number,
    updatePassReq: UpdatePassReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      reflectionApi.updateReflectionPassConfig(role, id, updatePassReq, options)
    ) as any
  },

  useUpdateReflectionPass(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        role,
        id,
        payload,
      }: {
        role: string
        id: number
        payload: UpdatePassReq
      }) => reflectionApi.updateReflectionPass(role, id, payload),
      mutationOptions
    )
  },

  updateSelfPassInloginConfig(
    id: number,
    updatePassReq: UpdatePassReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/reflection/reflections/{id}/self-password',
      {
        id,
      },
      null,
      updatePassReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          schoolId: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 正式用户修改正式用户密码
   *  - 登录状态
   * @param id
   * @param updatePassReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  updateSelfPassInlogin(
    id: number,
    updatePassReq: UpdatePassReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      reflectionApi.updateSelfPassInloginConfig(id, updatePassReq, options)
    ) as any
  },

  useUpdateSelfPassInlogin(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: UpdatePassReq }) =>
        reflectionApi.updateSelfPassInlogin(id, payload),
      mutationOptions
    )
  },

  updateSelfPassConfig(
    role: string,
    phone: string,
    updatePassReq: UpdatePassReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'patch',
      '/reflection/reflections/{phone}/self-password/{role}',
      {
        role,
        phone,
      },
      null,
      updatePassReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          schoolId: (v: any) => v || 0,
        },
      }
    )
  },

  /**
   * @summary 正式用户修改正式用户密码
   *  - 未登录状态
   * @param role
   * @param phone
   * @param updatePassReq
   * @param [options]
   * @return AxiosResponsePromise<boolean>
   */
  updateSelfPass(
    role: string,
    phone: string,
    updatePassReq: UpdatePassReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<boolean> {
    return axios(
      reflectionApi.updateSelfPassConfig(role, phone, updatePassReq, options)
    ) as any
  },

  useUpdateSelfPass(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        role,
        phone,
        payload,
      }: {
        role: string
        phone: string
        payload: UpdatePassReq
      }) => reflectionApi.updateSelfPass(role, phone, payload),
      mutationOptions
    )
  },

  queryStudentsConfig(
    queryStudentsQuery?: QueryStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/students',
      {},
      queryStudentsQuery
    )
  },

  /**
   * @summary 正式用户-学生-列表查询
   * @param [queryStudentsQuery]
   * @param [queryStudentsQuery.status] 状态查询
   * @param [queryStudentsQuery.expand]
   * @param [queryStudentsQuery.archivedAtNotEmpty] 传 true 查离校用户
   * @param [queryStudentsQuery.notificationId] 获取 expand:is_read_notification 时必须传
   * @param [queryStudentsQuery.isReadNotification] 是否读了某通知
   * @param [queryStudentsQuery.paginated] 是否分页，默认 1
   * @param [queryStudentsQuery.page] 显示的数据页，默认 1
   * @param [queryStudentsQuery.perPage] 每页显示数量，默认 20
   * @param [queryStudentsQuery.sort]
   * @param [queryStudentsQuery.nameIn]
   * @param [queryStudentsQuery.nameLike] 名字模糊查询
   * @param [queryStudentsQuery.accountIn] 多个账户查询
   * @param [queryStudentsQuery.accountLike] 账户模糊查询
   * @param [queryStudentsQuery.phone]
   * @param [queryStudentsQuery.phoneLike] 手机号模糊查询
   * @param [queryStudentsQuery.phoneIn]
   * @param [queryStudentsQuery.usinIn] 学工号查询
   * @param [queryStudentsQuery.usinLike] 学工号模糊查询
   * @param [queryStudentsQuery.createdAtEgt] 创建时间大于
   * @param [queryStudentsQuery.createdAtElt] 创建时间小于
   * @param [queryStudentsQuery.statusIn]
   * @param [queryStudentsQuery.roleIn]
   * @param [queryStudentsQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<Reflection[]>
   */
  queryStudents<TQuery extends QueryStudentsQuery>(
    queryStudentsQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Reflection, TQuery['expand']>[]> {
    return axios(
      reflectionApi.queryStudentsConfig(queryStudentsQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Reflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryStudents<TQuery extends QueryStudentsQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Reflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<Reflection, TQuery['expand']>[]>(
      reflectionApi.queryStudents,
      'Reflection.queryStudents',
      queryOptions,
      query
    )
  },

  createStudentConfig(
    reflectionAdd: ReflectionAdd,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/reflection/students',
      {},
      null,
      reflectionAdd,
      {
        format: 'json',
        isArray: false,
        defaults: {
          usin: (v: any) => v || '',
          role: (v: any) => v || null,
          email: (v: any) => v || '',
          avatar: (v: any) => v || '',
          photo: (v: any) => v || '',
          gender: (v: any) => v || null,
          status: (v: any) => v || null,
          idcard: (v: any) => v || '',
          idcardType: (v: any) => v || null,
          graduate: (v: any) => v || '',
          registedAt: (v: any) => v || '',
          admissionedAt: (v: any) => v || '',
          birthday: (v: any) => v || '',
          guardians: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 正式用户-学生-添加
   * @param reflectionAdd
   * @param [options]
   * @return AxiosResponsePromise<Reflection>
   */
  createStudent(
    reflectionAdd: ReflectionAdd,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Reflection> {
    return axios(
      reflectionApi.createStudentConfig(reflectionAdd, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Reflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateStudent(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: ReflectionAdd) => reflectionApi.createStudent(payload),
      mutationOptions
    )
  },

  batchDeleteStudentsConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/reflection/students/$batch',
      {},
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 正式用户-学生-批量删除
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDeleteStudents(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      reflectionApi.batchDeleteStudentsConfig(batchUpdateWithIdReq, options)
    ) as any
  },

  useBatchDeleteStudents(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: BatchUpdateWithIdReq) =>
        reflectionApi.batchDeleteStudents(payload),
      mutationOptions
    )
  },

  batchArchiveStudentsConfig(
    batchUpdateWithIdStatusReq: BatchUpdateWithIdStatusReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/reflection/students/archive/$batch',
      {},
      null,
      batchUpdateWithIdStatusReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 正式用户-学生-批量离校/入校
   * @param batchUpdateWithIdStatusReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchArchiveStudents(
    batchUpdateWithIdStatusReq: BatchUpdateWithIdStatusReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      reflectionApi.batchArchiveStudentsConfig(
        batchUpdateWithIdStatusReq,
        options
      )
    ) as any
  },

  useBatchArchiveStudents(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: BatchUpdateWithIdStatusReq) =>
        reflectionApi.batchArchiveStudents(payload),
      mutationOptions
    )
  },

  batchDisableStudentsConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    batchDisableStudentsQuery?: BatchDisableStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/reflection/students/disable/$batch',
      {},
      batchDisableStudentsQuery,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 正式用户-学生-批量禁用
   * @param batchUpdateWithIdReq
   * @param [batchDisableStudentsQuery]
   * @param [batchDisableStudentsQuery.disabled] 是否禁用-默认是
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDisableStudents(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    batchDisableStudentsQuery?: BatchDisableStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      reflectionApi.batchDisableStudentsConfig(
        batchUpdateWithIdReq,
        batchDisableStudentsQuery,
        options
      )
    ) as any
  },

  useBatchDisableStudents(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        payload,
        query,
      }: {
        payload: BatchUpdateWithIdReq
        query?: BatchDisableStudentsQuery
      }) => reflectionApi.batchDisableStudents(payload, query),
      mutationOptions
    )
  },

  queryExportedStudentsConfig(
    keys: string,
    idIn: string,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/reflection/students/exported', {
      keys,
      idIn,
    })
  },

  /**
   * @summary 正式用户-学生-列表导出
   * @param keys
   * @param idIn
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryExportedStudents(
    keys: string,
    idIn: string,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      reflectionApi.queryExportedStudentsConfig(keys, idIn, options)
    ) as any
  },

  useQueryExportedStudents(
    {
      keys,
      idIn,
    }: {
      keys: string
      idIn: string
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      reflectionApi.queryExportedStudents,
      'Reflection.queryExportedStudents',
      queryOptions,
      keys,
      idIn
    )
  },

  queryImportedStudentsConfig(
    queryImportedStudentsQuery?: QueryImportedStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/students/imported',
      {},
      queryImportedStudentsQuery
    )
  },

  /**
   * @summary 正式用户-学生-列表导入
   * @param [queryImportedStudentsQuery]
   * @param [queryImportedStudentsQuery.updating] 是否为批量更新，默认为否（即创建）
   * @param [queryImportedStudentsQuery.idIn] 批量更新时，需要传本字段
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryImportedStudents(
    queryImportedStudentsQuery?: QueryImportedStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      reflectionApi.queryImportedStudentsConfig(
        queryImportedStudentsQuery,
        options
      )
    ) as any
  },

  useQueryImportedStudents(
    query?: QueryImportedStudentsQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      reflectionApi.queryImportedStudents,
      'Reflection.queryImportedStudents',
      queryOptions,
      query
    )
  },

  importedStudentsConfig(
    uploadFile: UploadFile,
    importedStudentsQuery?: ImportedStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/reflection/students/imported',
      {},
      importedStudentsQuery,
      uploadFile,
      {
        format: 'form',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】导入学生用户
   * @param uploadFile
   * @param [importedStudentsQuery]
   * @param [importedStudentsQuery.updating] 是否为批量更新，默认为否（即创建）
   * @param [options]
   * @return AxiosResponsePromise<BatchResult>
   */
  importedStudents(
    uploadFile: UploadFile,
    importedStudentsQuery?: ImportedStudentsQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<BatchResult> {
    return axios(
      reflectionApi.importedStudentsConfig(
        uploadFile,
        importedStudentsQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new BatchResult(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useImportedStudents(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        payload,
        query,
      }: {
        payload: UploadFile
        query?: ImportedStudentsQuery
      }) => reflectionApi.importedStudents(payload, query),
      mutationOptions
    )
  },

  queryStudentsListConfig(
    queryStudentsListQuery?: QueryStudentsListQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/students/list',
      {},
      queryStudentsListQuery
    )
  },

  /**
   * @summary 正式用户-学生-列表查询
   * @param [queryStudentsListQuery]
   * @param [queryStudentsListQuery.status] 状态查询
   * @param [queryStudentsListQuery.expand]
   * @param [queryStudentsListQuery.paginated] 是否分页，默认 1
   * @param [queryStudentsListQuery.page] 显示的数据页，默认 1
   * @param [queryStudentsListQuery.perPage] 每页显示数量，默认 20
   * @param [queryStudentsListQuery.sort]
   * @param [queryStudentsListQuery.nameIn]
   * @param [queryStudentsListQuery.nameLike] 名字模糊查询
   * @param [queryStudentsListQuery.accountIn] 多个账户查询
   * @param [queryStudentsListQuery.accountLike] 账户模糊查询
   * @param [queryStudentsListQuery.phone]
   * @param [queryStudentsListQuery.phoneLike] 手机号模糊查询
   * @param [queryStudentsListQuery.phoneIn]
   * @param [queryStudentsListQuery.usinIn] 学工号查询
   * @param [queryStudentsListQuery.usinLike] 学工号模糊查询
   * @param [queryStudentsListQuery.createdAtEgt] 创建时间大于
   * @param [queryStudentsListQuery.createdAtElt] 创建时间小于
   * @param [queryStudentsListQuery.statusIn]
   * @param [queryStudentsListQuery.roleIn]
   * @param [queryStudentsListQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<Reflection[]>
   */
  queryStudentsList<TQuery extends QueryStudentsListQuery>(
    queryStudentsListQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Reflection, TQuery['expand']>[]> {
    return axios(
      reflectionApi.queryStudentsListConfig(queryStudentsListQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Reflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryStudentsList<TQuery extends QueryStudentsListQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Reflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<Reflection, TQuery['expand']>[]>(
      reflectionApi.queryStudentsList,
      'Reflection.queryStudentsList',
      queryOptions,
      query
    )
  },

  loadFormalSamePhoneConfig(
    reflectionPhone: ReflectionPhone,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/reflection/students/same-phone/guardians',
      {},
      null,
      reflectionPhone,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 正式用户-学生-相同手机号查询
   * @param reflectionPhone
   * @param [options]
   * @return AxiosResponsePromise<ReflectionPhoneRes>
   */
  loadFormalSamePhone(
    reflectionPhone: ReflectionPhone,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<ReflectionPhoneRes> {
    return axios(
      reflectionApi.loadFormalSamePhoneConfig(reflectionPhone, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new ReflectionPhoneRes(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadFormalSamePhone(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: ReflectionPhone) => reflectionApi.loadFormalSamePhone(payload),
      mutationOptions
    )
  },

  updateStudentConfig(
    id: number,
    reflectionReq: ReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/reflection/students/{id}',
      {
        id,
      },
      null,
      reflectionReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          usin: (v: any) => v || '',
          account: (v: any) => v || '',
          role: (v: any) => v || null,
          phone: (v: any) => v || '',
          email: (v: any) => v || '',
          avatar: (v: any) => v || '',
          photo: (v: any) => v || '',
          gender: (v: any) => v || null,
          status: (v: any) => v || null,
          idcard: (v: any) => v || '',
          idcardType: (v: any) => v || null,
          graduate: (v: any) => v || '',
          registedAt: (v: any) => v || '',
          birthday: (v: any) => v || '',
          guardians: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 正式用户-学生-单个编辑
   * @param id
   * @param reflectionReq
   * @param [options]
   * @return AxiosResponsePromise<Reflection>
   */
  updateStudent(
    id: number,
    reflectionReq: ReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Reflection> {
    return axios(
      reflectionApi.updateStudentConfig(id, reflectionReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Reflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateStudent(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: ReflectionReq }) =>
        reflectionApi.updateStudent(id, payload),
      mutationOptions
    )
  },

  loadStudentConfig(
    id: number,
    loadStudentQuery?: LoadStudentQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/students/{id}',
      {
        id,
      },
      loadStudentQuery
    )
  },

  /**
   * @summary 正式用户-学生-单个查询
   * @param id
   * @param [loadStudentQuery]
   * @param [loadStudentQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Reflection>
   */
  loadStudent<TQuery extends LoadStudentQuery>(
    id: number,
    loadStudentQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Reflection, TQuery['expand']>> {
    return axios(
      reflectionApi.loadStudentConfig(id, loadStudentQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Reflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadStudent<TQuery extends LoadStudentQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Reflection, TQuery['expand']>> {
    return useQueryApi<Expand<Reflection, TQuery['expand']>>(
      reflectionApi.loadStudent,
      'Reflection.loadStudent',
      queryOptions,
      id,
      query
    )
  },

  createTeacherConfig(
    reflectionAdd: ReflectionAdd,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'post',
      '/reflection/teachers',
      {},
      null,
      reflectionAdd,
      {
        format: 'json',
        isArray: false,
        defaults: {
          usin: (v: any) => v || '',
          role: (v: any) => v || null,
          email: (v: any) => v || '',
          avatar: (v: any) => v || '',
          photo: (v: any) => v || '',
          gender: (v: any) => v || null,
          status: (v: any) => v || null,
          idcard: (v: any) => v || '',
          idcardType: (v: any) => v || null,
          graduate: (v: any) => v || '',
          registedAt: (v: any) => v || '',
          admissionedAt: (v: any) => v || '',
          birthday: (v: any) => v || '',
          guardians: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 正式用户-老师-添加
   * @param reflectionAdd
   * @param [options]
   * @return AxiosResponsePromise<Reflection>
   */
  createTeacher(
    reflectionAdd: ReflectionAdd,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Reflection> {
    return axios(
      reflectionApi.createTeacherConfig(reflectionAdd, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Reflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useCreateTeacher(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: ReflectionAdd) => reflectionApi.createTeacher(payload),
      mutationOptions
    )
  },

  queryTeachersConfig(
    queryTeachersQuery?: QueryTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/teachers',
      {},
      queryTeachersQuery
    )
  },

  /**
   * @summary 正式用户-教师-列表查询
   * @param [queryTeachersQuery]
   * @param [queryTeachersQuery.status] 状态查询
   * @param [queryTeachersQuery.expand]
   * @param [queryTeachersQuery.nameOrPhoneLikes]
   * @param [queryTeachersQuery.archivedAtNotEmpty] 传 true 查离校用户
   * @param [queryTeachersQuery.notificationId] 获取 expand:is_read_notification 时必须传
   * @param [queryTeachersQuery.isReadNotification] 是否读了某通知
   * @param [queryTeachersQuery.paginated] 是否分页，默认 1
   * @param [queryTeachersQuery.page] 显示的数据页，默认 1
   * @param [queryTeachersQuery.perPage] 每页显示数量，默认 20
   * @param [queryTeachersQuery.sort]
   * @param [queryTeachersQuery.nameIn]
   * @param [queryTeachersQuery.nameLike] 名字模糊查询
   * @param [queryTeachersQuery.accountIn] 多个账户查询
   * @param [queryTeachersQuery.accountLike] 账户模糊查询
   * @param [queryTeachersQuery.phone]
   * @param [queryTeachersQuery.phoneLike] 手机号模糊查询
   * @param [queryTeachersQuery.phoneIn]
   * @param [queryTeachersQuery.usinIn] 学工号查询
   * @param [queryTeachersQuery.usinLike] 学工号模糊查询
   * @param [queryTeachersQuery.createdAtEgt] 创建时间大于
   * @param [queryTeachersQuery.createdAtElt] 创建时间小于
   * @param [queryTeachersQuery.statusIn]
   * @param [queryTeachersQuery.roleIn]
   * @param [queryTeachersQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<Reflection[]>
   */
  queryTeachers<TQuery extends QueryTeachersQuery>(
    queryTeachersQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Reflection, TQuery['expand']>[]> {
    return axios(
      reflectionApi.queryTeachersConfig(queryTeachersQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? _data.map((d: any) => new Reflection(d)) : _data
      return { data, ...rest }
    }) as any
  },

  useQueryTeachers<TQuery extends QueryTeachersQuery>(
    query?: TQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Reflection, TQuery['expand']>[]> {
    return useQueryApi<Expand<Reflection, TQuery['expand']>[]>(
      reflectionApi.queryTeachers,
      'Reflection.queryTeachers',
      queryOptions,
      query
    )
  },

  batchDeleteTeachersConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'delete',
      '/reflection/teachers/$batch',
      {},
      null,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 正式用户-教师-批量删除
   * @param batchUpdateWithIdReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDeleteTeachers(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      reflectionApi.batchDeleteTeachersConfig(batchUpdateWithIdReq, options)
    ) as any
  },

  useBatchDeleteTeachers(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: BatchUpdateWithIdReq) =>
        reflectionApi.batchDeleteTeachers(payload),
      mutationOptions
    )
  },

  batchArchiveTeachersConfig(
    batchUpdateWithIdStatusReq: BatchUpdateWithIdStatusReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/reflection/teachers/archive/$batch',
      {},
      null,
      batchUpdateWithIdStatusReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 正式用户-老师-批量离校/入校
   * @param batchUpdateWithIdStatusReq
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchArchiveTeachers(
    batchUpdateWithIdStatusReq: BatchUpdateWithIdStatusReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      reflectionApi.batchArchiveTeachersConfig(
        batchUpdateWithIdStatusReq,
        options
      )
    ) as any
  },

  useBatchArchiveTeachers(mutationOptions?: MutationOptions) {
    return useMutationApi(
      (payload: BatchUpdateWithIdStatusReq) =>
        reflectionApi.batchArchiveTeachers(payload),
      mutationOptions
    )
  },

  batchDisableTeachersConfig(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    batchDisableTeachersQuery?: BatchDisableTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/reflection/teachers/disable/$batch',
      {},
      batchDisableTeachersQuery,
      batchUpdateWithIdReq,
      {
        format: 'json',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 正式用户-教师-批量禁用/启用
   * @param batchUpdateWithIdReq
   * @param [batchDisableTeachersQuery]
   * @param [batchDisableTeachersQuery.disabled] 是否禁用-默认是
   * @param [options]
   * @return AxiosResponsePromise<number>
   */
  batchDisableTeachers(
    batchUpdateWithIdReq: BatchUpdateWithIdReq,
    batchDisableTeachersQuery?: BatchDisableTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<number> {
    return axios(
      reflectionApi.batchDisableTeachersConfig(
        batchUpdateWithIdReq,
        batchDisableTeachersQuery,
        options
      )
    ) as any
  },

  useBatchDisableTeachers(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        payload,
        query,
      }: {
        payload: BatchUpdateWithIdReq
        query?: BatchDisableTeachersQuery
      }) => reflectionApi.batchDisableTeachers(payload, query),
      mutationOptions
    )
  },

  queryExportedTeachersConfig(
    keys: string,
    idIn: string,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(options, 'get', '/reflection/teachers/exported', {
      keys,
      idIn,
    })
  },

  /**
   * @summary 正式用户-教师-列表导出
   * @param keys
   * @param idIn
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryExportedTeachers(
    keys: string,
    idIn: string,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      reflectionApi.queryExportedTeachersConfig(keys, idIn, options)
    ) as any
  },

  useQueryExportedTeachers(
    {
      keys,
      idIn,
    }: {
      keys: string
      idIn: string
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      reflectionApi.queryExportedTeachers,
      'Reflection.queryExportedTeachers',
      queryOptions,
      keys,
      idIn
    )
  },

  importedTeachersConfig(
    uploadFile: UploadFile,
    importedTeachersQuery?: ImportedTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/reflection/teachers/imported',
      {},
      importedTeachersQuery,
      uploadFile,
      {
        format: 'form',
        isArray: false,
        defaults: {},
      }
    )
  },

  /**
   * @summary 【后台】导入教师用户
   * @param uploadFile
   * @param [importedTeachersQuery]
   * @param [importedTeachersQuery.updating] 是否为批量更新，默认为否（即创建）
   * @param [options]
   * @return AxiosResponsePromise<BatchResult>
   */
  importedTeachers(
    uploadFile: UploadFile,
    importedTeachersQuery?: ImportedTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<BatchResult> {
    return axios(
      reflectionApi.importedTeachersConfig(
        uploadFile,
        importedTeachersQuery,
        options
      )
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new BatchResult(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useImportedTeachers(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({
        payload,
        query,
      }: {
        payload: UploadFile
        query?: ImportedTeachersQuery
      }) => reflectionApi.importedTeachers(payload, query),
      mutationOptions
    )
  },

  queryImportedTeachersConfig(
    queryImportedTeachersQuery?: QueryImportedTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/teachers/imported',
      {},
      queryImportedTeachersQuery
    )
  },

  /**
   * @summary 正式用户-教师-列表导入
   * @param [queryImportedTeachersQuery]
   * @param [queryImportedTeachersQuery.updating] 是否为批量更新，默认为否（即创建）
   * @param [queryImportedTeachersQuery.idIn] 批量更新时，需要传本字段
   * @param [options]
   * @return AxiosResponsePromise<null>
   */
  queryImportedTeachers(
    queryImportedTeachersQuery?: QueryImportedTeachersQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<null> {
    return axios(
      reflectionApi.queryImportedTeachersConfig(
        queryImportedTeachersQuery,
        options
      )
    ) as any
  },

  useQueryImportedTeachers(
    query?: QueryImportedTeachersQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<null> {
    return useQueryApi<null>(
      reflectionApi.queryImportedTeachers,
      'Reflection.queryImportedTeachers',
      queryOptions,
      query
    )
  },

  queryTeachersListConfig(
    queryTeachersListQuery?: QueryTeachersListQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/teachers/list',
      {},
      queryTeachersListQuery
    )
  },

  /**
   * @summary 正式用户-老师-列表查询
   * @param [queryTeachersListQuery]
   * @param [queryTeachersListQuery.status] 状态查询
   * @param [queryTeachersListQuery.expand]
   * @param [queryTeachersListQuery.paginated] 是否分页，默认 1
   * @param [queryTeachersListQuery.page] 显示的数据页，默认 1
   * @param [queryTeachersListQuery.perPage] 每页显示数量，默认 20
   * @param [queryTeachersListQuery.sort]
   * @param [queryTeachersListQuery.nameIn]
   * @param [queryTeachersListQuery.nameLike] 名字模糊查询
   * @param [queryTeachersListQuery.accountIn] 多个账户查询
   * @param [queryTeachersListQuery.accountLike] 账户模糊查询
   * @param [queryTeachersListQuery.phone]
   * @param [queryTeachersListQuery.phoneLike] 手机号模糊查询
   * @param [queryTeachersListQuery.phoneIn]
   * @param [queryTeachersListQuery.usinIn] 学工号查询
   * @param [queryTeachersListQuery.usinLike] 学工号模糊查询
   * @param [queryTeachersListQuery.createdAtEgt] 创建时间大于
   * @param [queryTeachersListQuery.createdAtElt] 创建时间小于
   * @param [queryTeachersListQuery.statusIn]
   * @param [queryTeachersListQuery.roleIn]
   * @param [queryTeachersListQuery.idIn]
   * @param [options]
   * @return AxiosResponsePromise<object[]>
   */
  queryTeachersList(
    queryTeachersListQuery?: QueryTeachersListQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<object[]> {
    return axios(
      reflectionApi.queryTeachersListConfig(queryTeachersListQuery, options)
    ) as any
  },

  useQueryTeachersList(
    query?: QueryTeachersListQuery,
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<object[]> {
    return useQueryApi<object[]>(
      reflectionApi.queryTeachersList,
      'Reflection.queryTeachersList',
      queryOptions,
      query
    )
  },

  loadTeacherConfig(
    id: number,
    loadTeacherQuery?: LoadTeacherQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'get',
      '/reflection/teachers/{id}',
      {
        id,
      },
      loadTeacherQuery
    )
  },

  /**
   * @summary 正式用户-教师-单个查询
   * @param id
   * @param [loadTeacherQuery]
   * @param [loadTeacherQuery.expand]
   * @param [options]
   * @return AxiosResponsePromise<Reflection>
   */
  loadTeacher<TQuery extends LoadTeacherQuery>(
    id: number,
    loadTeacherQuery?: TQuery,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Expand<Reflection, TQuery['expand']>> {
    return axios(
      reflectionApi.loadTeacherConfig(id, loadTeacherQuery, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Reflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useLoadTeacher<TQuery extends LoadTeacherQuery>(
    {
      id,
      query,
    }: {
      id: number
      query?: TQuery
    },
    queryOptions?: QueryOptions
  ): UseQueryApiReturns<Expand<Reflection, TQuery['expand']>> {
    return useQueryApi<Expand<Reflection, TQuery['expand']>>(
      reflectionApi.loadTeacher,
      'Reflection.loadTeacher',
      queryOptions,
      id,
      query
    )
  },

  updateTeacherConfig(
    id: number,
    reflectionReq: ReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosRequestConfig {
    return buildConfig(
      options,
      'put',
      '/reflection/teachers/{id}',
      {
        id,
      },
      null,
      reflectionReq,
      {
        format: 'json',
        isArray: false,
        defaults: {
          name: (v: any) => v || '',
          usin: (v: any) => v || '',
          account: (v: any) => v || '',
          role: (v: any) => v || null,
          phone: (v: any) => v || '',
          email: (v: any) => v || '',
          avatar: (v: any) => v || '',
          photo: (v: any) => v || '',
          gender: (v: any) => v || null,
          status: (v: any) => v || null,
          idcard: (v: any) => v || '',
          idcardType: (v: any) => v || null,
          graduate: (v: any) => v || '',
          registedAt: (v: any) => v || '',
          birthday: (v: any) => v || '',
          guardians: (v: any) => v || [],
        },
      }
    )
  },

  /**
   * @summary 非正式用户-教师-单个编辑
   * @param id
   * @param reflectionReq
   * @param [options]
   * @return AxiosResponsePromise<Reflection>
   */
  updateTeacher(
    id: number,
    reflectionReq: ReflectionReq,
    options?: AxiosRequestExtraConfig
  ): AxiosResponsePromise<Reflection> {
    return axios(
      reflectionApi.updateTeacherConfig(id, reflectionReq, options)
    ).then(({ data: _data, ...rest }) => {
      const data = _data ? new Reflection(_data) : _data
      return { data, ...rest }
    }) as any
  },

  useUpdateTeacher(mutationOptions?: MutationOptions) {
    return useMutationApi(
      ({ id, payload }: { id: number; payload: ReflectionReq }) =>
        reflectionApi.updateTeacher(id, payload),
      mutationOptions
    )
  },
}

// generate uuid for every api function, useful for result-caching & other things
Object.keys(reflectionApi).forEach((api) => {
  // @ts-ignore
  reflectionApi[api]._name_ = `Reflection.${api}`
})
